/**
 * CRM Settings → Email inboxes: the Smilepass mailboxes the CRM reads and sends
 * through. Mail between a clinic's contacts and ANY of these shows up in that
 * office's Conversation tab.
 *
 * Adding an inbox is recording its address — the app reaches mailboxes through
 * its Azure grant, so there is no sign-in step; "Check access" proves the grant
 * before the first sync. "Keeps" is the privacy switch: a personal inbox should
 * keep only mail with our contacts, so the rest of that person's mail never
 * enters the database.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Button, Empty, Input, Popconfirm, Select, Switch, Table, Tag, Tooltip, message } from "antd";
import { PlusOutlined, WarningOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

import * as api from "./controller";
import "./leadSettings.css";
import "./mailboxes.css";

dayjs.extend(relativeTime);

const KEEP_OPTIONS = [
  { value: "all", label: "Everything" },
  { value: "matched", label: "Only mail with our contacts" },
];

const HISTORY_OPTIONS = [
  { value: 0, label: "All past mail" },
  { value: 365, label: "Last 12 months" },
  { value: 90, label: "Last 90 days" },
  { value: 30, label: "Last 30 days" },
];

const EMPTY_DRAFT = { address: "", name: "", keep: "all", history: 0 };

function Status({ m }) {
  if (!m.is_active) return <span className="crm-status-pill off">Off</span>;
  if (m.last_error) {
    return (
      <Tooltip title={m.last_error}>
        <span className="mbx-error">
          <WarningOutlined /> Last sync failed {m.last_error_at ? dayjs(m.last_error_at).fromNow() : ""}
        </span>
      </Tooltip>
    );
  }
  if (m.first_sync_pending) {
    return <span className="crm-sub">Waiting for its first sync (every 5 minutes)</span>;
  }
  return (
    <Tooltip title={m.last_polled_at ? dayjs(m.last_polled_at).format("MMM D, YYYY HH:mm") : null}>
      <span className="mbx-ok">Synced {m.last_polled_at ? dayjs(m.last_polled_at).fromNow() : ""}</span>
    </Tooltip>
  );
}

export default function MailboxesPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(""); // "<id>:<action>"

  const load = useCallback(async () => {
    setLoading(true);
    const r = await api.getMailboxes();
    const ok = r.status >= 200 && r.status < 300 && Array.isArray(r.json);
    setFailed(!ok);
    setRows(ok ? r.json : []);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const check = async (m) => {
    setBusy(`${m.id}:check`);
    const r = await api.checkMailbox(m.id);
    setBusy("");
    if (r.status >= 200 && r.status < 300 && r.json && r.json.ok) {
      message.success(`${m.mailbox_address} is reachable — ${r.json.inbox} in Inbox, ${r.json.sent} in Sent.`, 5);
    } else {
      const j = r.json || {};
      message.error(
        `Can't read ${m.mailbox_address}${j.status ? ` (${j.status})` : ""}. `
        + "The Smilepass app in Azure needs access to this mailbox.",
        8,
      );
    }
  };

  const update = async (m, patch, okText) => {
    setBusy(`${m.id}:update`);
    const r = await api.updateMailbox(m.id, patch);
    setBusy("");
    if (r.status >= 200 && r.status < 300) {
      if (okText) message.success(okText);
      load();
    } else {
      message.error((r.json && r.json.detail) || "Couldn't save that.");
    }
  };

  const add = async () => {
    const address = draft.address.trim();
    if (!address) { message.error("Enter the inbox's email address."); return; }
    setAdding(true);
    const r = await api.createMailbox({
      mailbox_address: address,
      display_name: draft.name.trim(),
      store_unmatched: draft.keep === "all",
      history_days: draft.history || null,
    });
    setAdding(false);
    if (r.status >= 200 && r.status < 300 && r.json) {
      setDraft(EMPTY_DRAFT);
      message.success("Inbox added. Its first sync runs within five minutes.");
      await load();
      check(r.json);   // prove the Azure grant straight away
    } else {
      message.error((r.json && r.json.detail) || "Couldn't add the inbox.");
    }
  };

  const columns = [
    {
      title: "Inbox",
      render: (_, m) => (
        <div>
          <div className="mbx-address">{m.mailbox_address}</div>
          <div className="mbx-tags">
            {m.display_name ? <span className="crm-sub">{m.display_name}</span> : null}
            {m.is_default_sender ? (
              <Tooltip title="Reminders, booking emails, reports and new emails with no inbox picked go out from here.">
                <Tag color="purple" style={{ margin: 0 }}>Default sender</Tag>
              </Tooltip>
            ) : null}
            {m.kind === "ftc" ? (
              <Tag style={{ margin: 0 }}>FTC{m.owner ? ` · ${m.owner.name || m.owner.email}` : ""}</Tag>
            ) : null}
          </div>
        </div>
      ),
    },
    {
      title: "Keeps", width: 230,
      render: (_, m) => m.kind !== "crm" ? (
        <span className="crm-sub">Everything</span>
      ) : (
        <Tooltip
          title={m.store_unmatched
            ? "All mail is kept, so adding a contact later files earlier mail under their office."
            : "Only mail with a CRM contact or lead on it is kept. The rest never enters Smilepass — right for a personal inbox."}
        >
          <Select
            size="small"
            value={m.store_unmatched ? "all" : "matched"}
            options={KEEP_OPTIONS}
            style={{ width: 210 }}
            disabled={busy === `${m.id}:update`}
            onChange={(v) => update(m, { store_unmatched: v === "all" },
              v === "all" ? "Keeping all mail from now on." : "Keeping only mail with our contacts from now on.")}
          />
        </Tooltip>
      ),
    },
    {
      title: "Creates leads", width: 120, align: "center",
      render: (_, m) => (m.kind !== "crm" ? <span className="crm-sub">—</span> : (
        <Tooltip
          title={m.creates_leads
            ? `New people this inbox emails with become leads (from ${m.leads_since ? dayjs(m.leads_since).format("MMM D, YYYY") : "now"}).`
              + (m.store_unmatched ? "" : " On a contacts-only inbox that means their mail is kept too.")
            : "Off: mail is still filed under existing offices and leads, it just never makes new ones."}
        >
          <Switch
            size="small"
            checked={!!m.creates_leads}
            disabled={busy === `${m.id}:update` || !m.is_active}
            onChange={(v) => update(m, { creates_leads: v },
              v ? "New correspondents will become leads." : "This inbox no longer creates leads.")}
          />
        </Tooltip>
      )),
    },
    { title: "Status", width: 230, render: (_, m) => <Status m={m} /> },
    {
      title: "Emails", width: 80, align: "right",
      render: (_, m) => <span className="leadset-num">{m.message_count || 0}</span>,
    },
    {
      title: "", key: "a", align: "right", width: 190,
      render: (_, m) => (
        <>
          <Button size="small" type="link" loading={busy === `${m.id}:check`} onClick={() => check(m)}>
            Check access
          </Button>
          {m.kind !== "crm" ? null : m.is_active ? (
            <Popconfirm
              title="Stop using this inbox?"
              description="It stops being read and offered as a sender. Its mail stays in every office's history."
              okText="Turn off"
              okButtonProps={{ danger: true }}
              onConfirm={() => update(m, { is_active: false }, "Inbox turned off.")}
            >
              <Button size="small" type="link" danger>Turn off</Button>
            </Popconfirm>
          ) : (
            <Button size="small" type="link" onClick={() => update(m, { is_active: true }, "Inbox turned back on.")}>
              Turn on
            </Button>
          )}
        </>
      ),
    },
  ];

  return (
    <div className="leadset-panel">
      <p className="crm-sub leadset-panel-sub">
        Mail between a clinic&rsquo;s contacts and any of these inboxes shows up in that
        office&rsquo;s Conversation tab. Each inbox is checked every five minutes; a reply
        goes out from the inbox that received the email. FTC inboxes are set up on the
        FTC Users page.
      </p>
      <Table
        size="small" rowKey="id" pagination={false}
        loading={loading}
        dataSource={rows}
        columns={columns}
        rowClassName={(m) => (m.is_active ? "" : "mbx-row-off")}
        locale={{
          emptyText: loading ? null : failed ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <>
                  <div>Couldn&rsquo;t load the inboxes.</div>
                  <Button size="small" style={{ marginTop: 10 }} onClick={load}>Try again</Button>
                </>
              }
            />
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No inboxes yet. Add one below." />
          ),
        }}
      />
      <div className="leadset-tag-add">
        <Input
          placeholder="Inbox address, e.g. sales@smilepass.com"
          value={draft.address}
          onChange={(e) => setDraft((d) => ({ ...d, address: e.target.value }))}
          onPressEnter={add}
          style={{ width: 250 }}
        />
        <Input
          placeholder="Name (optional)"
          value={draft.name}
          onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
          onPressEnter={add}
          style={{ width: 150 }}
        />
        <Tooltip title="Choose “Only mail with our contacts” for a person's own inbox.">
          <Select
            value={draft.keep}
            options={KEEP_OPTIONS}
            onChange={(v) => setDraft((d) => ({ ...d, keep: v }))}
            style={{ width: 220 }}
          />
        </Tooltip>
        <Tooltip title="How far back the first sync reaches.">
          <Select
            value={draft.history}
            options={HISTORY_OPTIONS}
            onChange={(v) => setDraft((d) => ({ ...d, history: v }))}
            style={{ width: 150 }}
          />
        </Tooltip>
        <Button type="primary" icon={<PlusOutlined />} loading={adding} onClick={add}>Add inbox</Button>
      </div>
    </div>
  );
}
